import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useRef, useState } from 'react';

import type { ApiError } from '@/shared/api/errors';

import { type CatalogListParams, catalogKeys, catalogsApi, putToBucket } from './api';
import type { Catalog } from './types';

/** In the worker's hands: every two seconds. Never once it is ready or failed, and not
 * one that looks stuck: it will not move by itself, and the screen offers to ask again. */
function pollWhile(catalogs: readonly Catalog[] | undefined): number | false {
  const working = (catalogs ?? []).filter((each) => each.status === 'preparing' && !each.stuck);
  return working.length > 0 ? 2_000 : false;
}

/**
 * `poll` for the supplier's file, where a catalog just uploaded is being prepared. The
 * shelf of «Documentos» only shows the ready ones: it does not ask every two seconds for
 * one it would not show.
 */
export function useCatalogs(params: CatalogListParams, { poll = true } = {}) {
  return useQuery({
    queryKey: catalogKeys.list(params),
    queryFn: () => catalogsApi.list(params),
    refetchInterval: poll ? (query) => pollWhile(query.state.data?.items) : false,
  });
}

export function useCatalog(id: string | undefined) {
  return useQuery<Catalog, ApiError>({
    queryKey: catalogKeys.detail(id ?? ''),
    queryFn: () => catalogsApi.get(id ?? ''),
    enabled: Boolean(id),
    refetchInterval: (query) => pollWhile(query.state.data ? [query.state.data] : undefined),
    // Open, a ready catalog has nothing to ask the API: coming back to the tab must not
    // fetch it again (and a failure then would close what is being read).
    refetchOnWindowFocus: (query) => query.state.data?.status !== 'ready',
  });
}

function useCatalogMutation<TVariables, TResult = Catalog>(
  mutationFn: (variables: TVariables) => Promise<TResult>,
  after?: (result: TResult) => void,
) {
  const client = useQueryClient();
  return useMutation<TResult, unknown, TVariables>({
    mutationFn,
    onSuccess: (result) => {
      if (result && typeof result === 'object' && 'id' in result) {
        client.setQueryData(catalogKeys.detail((result as unknown as Catalog).id), result);
      }
      after?.(result);
    },
    // Success or not: a refusal says something changed under the screen (somebody else
    // deleted it, the worker finished meanwhile).
    onSettled: () => void client.invalidateQueries({ queryKey: catalogKeys.all }),
  });
}

export interface UploadProgress {
  loaded: number;
  total: number;
}

/**
 * Three steps: the API makes the row and signs a link, the browser sends the file to the
 * bucket, and the API is told so (it looks at what arrived and the worker prepares it).
 * When the file does not reach the bucket (a cancel, the line), the row is deleted at once
 * so a half upload does not wait for the retention. When the last step fails the row is
 * left alone: the API may have taken it before the answer was lost, and a catalog still
 * `uploading` never shows in a list and goes with the retention.
 */
export function useUploadCatalog(supplierId: string) {
  const [progress, setProgress] = useState<UploadProgress | null>(null);
  const controller = useRef<AbortController | null>(null);
  const mutation = useCatalogMutation<{ file: File; name: string | null }>(
    async ({ file, name }) => {
      const abort = new AbortController();
      controller.current = abort;
      setProgress({ loaded: 0, total: file.size });
      try {
        const started = await catalogsApi.start(supplierId, file, name);
        try {
          await putToBucket(started.upload, file, {
            signal: abort.signal,
            onProgress: (loaded, total) => setProgress({ loaded, total }),
          });
        } catch (error) {
          void catalogsApi.remove(started.catalog.id).catch(() => undefined);
          throw error;
        }
        return await catalogsApi.uploaded(started.catalog.id);
      } finally {
        controller.current = null;
        setProgress(null);
      }
    },
  );
  return {
    ...mutation,
    progress,
    cancel: () => controller.current?.abort(),
  };
}

export function useRenameCatalog(id: string) {
  return useCatalogMutation<string>((name) => catalogsApi.rename(id, name));
}

export function useDeleteCatalog(id: string) {
  const client = useQueryClient();
  return useCatalogMutation<void, void>(
    () => catalogsApi.remove(id),
    () => client.removeQueries({ queryKey: catalogKeys.detail(id) }),
  );
}

export function useRetryCatalog(id: string) {
  return useCatalogMutation<void>(() => catalogsApi.retry(id));
}
